import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom"
import { Cluster, ClusterNodeNote } from "../../../models/Cluster";
import { Clusters } from "../../../api/Clusters";
import { useToasts } from "../../../contexts/toasts";
import { Alert, Badge, Breadcrumb, Button, Card, Col, Container, Modal, Row, Table } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { convertKiBtoGiB, formatDate, renderConditionStatus } from "../../../utils";
import { Flex, Pagination, Tabs, TabsProps } from "antd";
import { debounce } from "lodash";
import { PagedResults } from "../../../models/generics";
import AppSearch from "../../../components/common/AppSearch";
import { useFullScreenLoader } from "../../../contexts/fullScreenLoader";
import NoteForm from "../../../components/forms/NoteForm";
import NoteItem from "../../../components/list-items/NoteItem";
import AppTag from "../../../components/common/AppTag";
import AppLoader from "../../../components/common/AppLoader";

export default function ClusterNodeDetail() {
  const id = window.__env.DEFAULT_CLUSTER_ID || 0;
  const { nodeId } = useParams();
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const [cluster, setCluster] = useState<Cluster>();
  const [node, setNode] = useState<any>();
  const [notes, setNotes] = useState<ClusterNodeNote[]>();
  const [error, setError] = useState(false);
  const [selectedTab, setSelectedTab] = useState<"nodeInfo" | "notes">("nodeInfo");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [totalNotes, setTotalNotes] = useState<number>();
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<ClusterNodeNote>();

  const initClusterNode = useCallback(() => {
    Clusters.getCluster(+id!).then(
      ({ data }) => {
        setCluster(data);
        if (!data.json || !data.json.items) setError(true);
        const currentNode = data.json.items.find((node: any) => node.metadata.uid === nodeId);
        if (currentNode) setNode(currentNode);
        else setError(true);
      },
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('cluster-node-error', message, `Cluster Node Error ${status ? `(${status})` : ''}`, 'danger');
        setError(true);
      }
    )
  }, [id, nodeId, showToast]);

  const initNodeNotes = useCallback(() => {
    const params = new URLSearchParams();
    params.append("nodeId", nodeId!);
    params.set("page", `${currentPage}`);
    params.set("size", `${20}`);
    params.set("sort", "id");
    params.set("desc", `${true}`);
    if (searchText.length) params.set("freeText", searchText);
    Clusters.getNotes(params).then(
      ({ data }) => {
        setNotes((data as PagedResults<ClusterNodeNote>).content);
        setTotalNotes((data as PagedResults<ClusterNodeNote>).pageMetadata.totalElements);
      },
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('notes-error', message, `Notes Error ${status ? `(${status})` : ''}`, 'danger');
        setError(true);
      }
    )
  }, [showToast, nodeId, currentPage, searchText]);

  useEffect(() => {
    if (id && nodeId) initClusterNode();
  }, [id, nodeId, initClusterNode]);

  const reset = useCallback(() => {
    setNotes(undefined);
    setCurrentPage(1);
    setTotalNotes(undefined);
    initNodeNotes();
  }, [initNodeNotes]);

  useEffect(() => {
    if (node) reset();
  }, [node, reset, currentPage, searchText]);

  const handleNoteModalClose = useCallback(() => {
    setSelectedNote(undefined);
    setShowNoteModal(false);
  }, []);

  const handleEditNote = useCallback((note?: ClusterNodeNote) => () => {
    setSelectedNote(note);
    setShowNoteModal(true);
  }, []);

  const handleDeleteNoteModal = useCallback((note: ClusterNodeNote) => () => {
    setSelectedNote(note);
    setShowDeleteNoteModal(true);
  }, []);

  const handleDeleteNoteModalClose = useCallback(() => {
    setSelectedNote(undefined);
    setShowDeleteNoteModal(false);
  }, []);

  const handleNoteSubmit = useCallback((values: any) => {
    showLoader();
    const body = {
      ...values,
      nodeId: nodeId!,
      clusterId: +id!,
    }
    const promise = selectedNote ? Clusters.editNote(selectedNote.id!, body) : Clusters.createNote(body);

    promise.then(
      () => {
        hideLoader();
        showToast('note-success', `Note ${selectedNote ? 'updated' : 'created'} successfully`, 'Note Success', 'success');
        handleNoteModalClose();
        reset();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('note-error', message, `Note Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [showLoader, showToast, hideLoader, reset, handleNoteModalClose, id, nodeId, selectedNote]);

  const handleTabChange = useCallback((tab: string | null) => setSelectedTab(tab as "nodeInfo" | "notes"), []);

  const debounceNotesSearch = useMemo(() => debounce(value => setSearchText(value), 500), []);

  useEffect(() => {
    return () => {
      debounceNotesSearch.cancel();
    };
  }, [debounceNotesSearch]);

  const renderNotes = useCallback(() => {
    if (!notes) return <AppLoader className="py-5" justify="center" size={60} />;

    if (notes.length === 0) return (
      <Alert className="py-2 m-2">
        <FontAwesomeIcon icon={solid('info-circle')} />
        <span className="ms-2">No Notes found.</span>
      </Alert>
    );

    return (
      <Row className="p-2 mx-0 gx-3">
        {notes.map(item => (
          <Col key={item.id!} md={3} sm={6}>
            <NoteItem note={item} onEdit={handleEditNote(item)} onDelete={handleDeleteNoteModal(item)} />
          </Col>
        ))}
      </Row>
    )
  }, [notes, handleDeleteNoteModal, handleEditNote]);

  const items: TabsProps["items"] = useMemo(() => ([
    {
      key: "nodeInfo",
      label: "Node Info",
      icon: <FontAwesomeIcon icon={solid("info-circle")} />,
      children:
        <Table>
          <thead>
            <tr>
              <th className="border-0" style={{ width: 200 }}></th>
              <th className="border-0"></th>
            </tr>
          </thead>
          {node ? (
            <tbody>
              {Object.entries(node.status.nodeInfo || {}).map(([key, value]: [key: string, value: any], index) => (
                <tr key={index}>
                  <th scope="col">{key}</th>
                  <td className="text-end">{value}</td>
                </tr>
              ))}
            </tbody>
          ) : (
            <Alert variant="info">
              <FontAwesomeIcon icon={solid('info-circle')} />
              <span className="ms-2">No Node Infos</span>
            </Alert>
          )}
        </Table>
    },
    {
      key: "notes",
      label: "Notes",
      icon: <FontAwesomeIcon icon={solid('note-sticky')} />,
      children:
        <>
          <Row className="py-1 px-2 mt-2 align-items-center">
            <Col md={3} className="pe-4">
              <AppSearch placeholder={`Search Notes...`} onChange={debounceNotesSearch} />
            </Col>

            <Col>
              <Pagination
                simple
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                total={totalNotes}
                pageSize={20}
                current={currentPage}
                onChange={setCurrentPage}
                className="d-flex align-items-center justify-content-center"
              />
            </Col>

            <Col md={3} className="text-end">
              <Button variant="success" size="sm" onClick={handleEditNote()}>
                <FontAwesomeIcon icon={solid('plus')} />
                <span className="ms-2">New Note</span>
              </Button>
            </Col>
          </Row>
          {renderNotes()}
        </>
    },
  ]), [node, currentPage, debounceNotesSearch, totalNotes, renderNotes, handleEditNote]);

  const nodeLabels = useMemo(() => {

    if (!node) return [];
    const keywords = ['arch', 'hostname', 'role'];

    const matchedLabels = Object.entries(node.metadata.labels)
      .filter(([key]) => keywords.some((word) => key.includes(word))).map(([key, value]) => (`${key}:${value}`));

    return matchedLabels;
  }, [node]);

  const readyItem = useMemo(() => {
    return node?.status.conditions.find((item: any) => item.type === "Ready");
  }, [node]);

  const renderNode = useCallback(() => (
    <Card className="item-detail-group main bg-cluster-node">
      <FontAwesomeIcon className="item-icon" icon={solid("circle-nodes")} />

      <div className="px-2 pt-2">
        <div className="p-2">
          <Row className="gx-0 align-items-center">
            <Col className="pe-3">
              <h3 className="mb-0">{node!.metadata.name}</h3>
              <Flex gap={16} align="center" className="my-2">
                <div>
                  <FontAwesomeIcon icon={solid("microchip")} fixedWidth />
                  <span className="ms-1">{node.status.capacity.cpu} CPUs</span>
                </div>
                <div>
                  <FontAwesomeIcon icon={solid("memory")} fixedWidth />
                  <span className="ms-1">{convertKiBtoGiB(node.status.capacity.memory)}</span>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {!!readyItem && (
                    <Badge className="bg-light bg-opacity-50 text-dark service-version border" pill={true}>
                      <span className="align-items-center">
                        {readyItem.type} {renderConditionStatus(readyItem, true)}
                      </span>
                    </Badge>
                  )}
                  {node.status.conditions.filter((item: any) => item.type !== "Ready" && !["PID"].some((word: string) => item.type.includes(word))).map((condition: any, index: number) => (
                    <Badge key={index} className="bg-light bg-opacity-50 text-dark border" pill={true}>
                      <span className="align-items-center text-center">
                        {condition.type} {renderConditionStatus(condition)}
                      </span>
                    </Badge>
                  ))}
                </div>
              </Flex>
            </Col>
          </Row>
        </div>

        <Flex className="tags-wrapper">
          {nodeLabels.map(tag => <AppTag className="me-1 shadow-sm" color="blue-inverse" tag={tag} key={tag} />)}
        </Flex>
        <div className="item-dates mb-2">
          <div className="d-inline-block">
            <b>Created:</b>
            <span className="ms-1">{formatDate(node!.metadata.creationTimestamp)}</span>
          </div>
        </div>
      </div>
    </Card>
  ), [node, nodeLabels, readyItem]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred during cluster retrieve. Try again later.</span>
      </Alert>
    );

    if (!cluster || !node) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel' }}>Control Panel</Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel/cluster-management' }}>Cluster Management</Breadcrumb.Item>
          {/* <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/control-panel/clusters-management/${id}` }}>{cluster.name}</Breadcrumb.Item> */}
          <Breadcrumb.Item active>{node.metadata.name}</Breadcrumb.Item>
        </Breadcrumb>

        {renderNode()}

        <Card className="item-detail-group mb-3">
          <Tabs
            activeKey={selectedTab}
            onChange={handleTabChange}
            defaultActiveKey="nodeInfo"
            items={items}
          />
        </Card>
      </>
    )
  }, [error, node, cluster, renderNode, items, selectedTab, handleTabChange]);

  const deleteNote = useCallback(() => {
    showLoader();

    Clusters.deleteNote(selectedNote!.id!)
      .then(
        () => {
          hideLoader();
          handleDeleteNoteModalClose();
          reset();
          showToast('note-success', `Note deleted successfully`, `Cluster Success`, 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('note-error', message, `Note Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleDeleteNoteModalClose, selectedNote, reset, showToast]);

  return (
    <Container className="mt-3">
      <Helmet>
        <title>Cluster Node Detail</title>
      </Helmet>

      {renderAsyncContent()}

      <Modal show={showNoteModal} onHide={handleNoteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedNote ? "Update" : "Create New"} Note</Modal.Title>
        </Modal.Header>
        <NoteForm note={selectedNote} onCancel={handleNoteModalClose} onSubmit={handleNoteSubmit} />
      </Modal>

      <Modal show={showDeleteNoteModal} onHide={handleDeleteNoteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete Note <b>{selectedNote?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteNoteModalClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteNote}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}